/**
 * AssessorWorkpaper — /w/:slug/app/assessor/workpaper/:engagementId
 *
 * PR-Cluster-6-l (docs/ux-design-plan.md §10). The engagement's
 * workpaper — one-page audit summary rendering every verdict,
 * dispute, Q&A thread + evidence request that ran under this
 * engagement. Closes the last deferred item from PR-Cluster-6-h
 * ("assessor workpaper dispute rendering with a `workspace
 * dissent` chip on each disputed row").
 *
 * Print-first: single scroll, no infinite scroll, no lazy loads
 * that block the print button. Cmd-P / Ctrl-P produces a
 * workpaper-shaped PDF via the browser's native print — no
 * jsPDF path yet; the browser's stylesheet is what powers the
 * printed output.
 *
 * Workspace-scoped: mounted under /w/:slug/app/* so
 * workspaceContext + assessor-session middleware gate the reads.
 * The assessor deep-links here from AssessorInbox.tsx after
 * picking a workspace to work in.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import { useQuery } from '@tanstack/react-query';
import { Alert, PageHeader, Spinner, tokens } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { queryKeys } from '../lib/query-keys';
import {
  getActiveMethodology,
  getAssessorEngagement,
  listAnnotationsByEngagement,
  listEvidenceRequestsForEngagement,
  listQaThreads,
  type ActiveMethodology,
  type AssessorAnnotationDispute,
  type AssessorAnnotationRow,
  type AssessorAnnotationVerdict,
  type AssessorEngagementRow,
  type EvidenceRequestRow,
  type QaThreadRow,
} from '../lib/api';
import { fmtDate } from '../lib/format';

type EngagementPhase = 'active' | 'read_only' | 'archive_expired' | 'revoked';

// Duplicated from AssessorEngagements.tsx / AssessorInbox.tsx so the
// workpaper doesn't couple to their internals. When phaseOf is
// promoted to a shared util, replace the three copies at once.
function phaseOf(e: {
  revoked_at: string | null;
  engagement_end: string;
  archive_window_days: number;
}, now: Date = new Date()): EngagementPhase {
  if (e.revoked_at) return 'revoked';
  const nowMs = now.getTime();
  if (nowMs <= Date.parse(e.engagement_end)) return 'active';
  const archiveMs =
    Date.parse(e.engagement_end) + e.archive_window_days * 24 * 60 * 60 * 1000;
  if (nowMs <= archiveMs) return 'read_only';
  return 'archive_expired';
}

export function AssessorWorkpaper() {
  const { engagementId = '' } = useParams<{ engagementId: string }>();
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;

  const engagementQuery = useQuery({
    queryKey: queryKeys.assessorWorkpaper(slug ?? '', engagementId),
    queryFn: () => getAssessorEngagement(slug!, engagementId),
    enabled: !!slug && !!engagementId,
  });
  const annotationsQuery = useQuery({
    queryKey: queryKeys.assessorAnnotations(slug ?? '', engagementId),
    queryFn: () => listAnnotationsByEngagement(slug!, engagementId),
    enabled: !!slug && !!engagementId,
  });
  const qaQuery = useQuery({
    queryKey: queryKeys.qaThreads(slug ?? '', engagementId),
    queryFn: () => listQaThreads(slug!, { engagement_id: engagementId }),
    enabled: !!slug && !!engagementId,
  });
  const requestsQuery = useQuery({
    queryKey: queryKeys.evidenceRequestsForEngagement(slug ?? '', engagementId),
    queryFn: () => listEvidenceRequestsForEngagement(slug!, engagementId),
    enabled: !!slug && !!engagementId,
  });
  const engagement = {
    data: engagementQuery.data ?? null,
    error: engagementQuery.error?.message ?? null,
    loading: engagementQuery.isLoading,
  };
  const annotations = {
    data: annotationsQuery.data ?? null,
    error: annotationsQuery.error?.message ?? null,
    loading: annotationsQuery.isLoading,
  };
  const qa = {
    data: qaQuery.data ?? null,
    error: qaQuery.error?.message ?? null,
    loading: qaQuery.isLoading,
  };
  const requests = {
    data: requestsQuery.data ?? null,
    error: requestsQuery.error?.message ?? null,
    loading: requestsQuery.isLoading,
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const loading =
    engagement.loading || annotations.loading || qa.loading || requests.loading;
  const anyError = engagement.error || annotations.error || qa.error || requests.error;

  if (!slug || !engagementId) return null;
  if (loading) return <Spinner label="Loading workpaper" />;
  if (anyError) return <Alert variant="error">{anyError}</Alert>;
  if (!engagement.data || !annotations.data || !qa.data || !requests.data) return null;

  const e = engagement.data.engagement;
  const rows = annotations.data.annotations;
  const disputes = annotations.data.active_disputes ?? {};
  const threads = qa.data.threads;
  const reqs = requests.data.requests;
  const phase = phaseOf(e);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '72rem' }}>
      <PageHeader
        title="Engagement workpaper"
        subtitle={`${workspaceCtx?.workspace.name ?? slug} · ${e.assessor_firm}`}
        right={
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={async () => {
                if (!slug) return;
                setDownloadingPdf(true);
                setPdfError(null);
                try {
                  const methodology = await getActiveMethodology(slug);
                  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
                  renderWorkpaperPdf(doc, {
                    workspaceName: workspaceCtx?.workspace.name ?? slug,
                    workspaceSlug: slug,
                    engagement: e,
                    phase,
                    annotations: rows,
                    disputes,
                    threads,
                    requests: reqs,
                    methodology,
                  });
                  doc.save(
                    `workpaper-${e.engagement_id}-${new Date().toISOString().slice(0, 10)}.pdf`,
                  );
                } catch (err) {
                  setPdfError(err instanceof Error ? err.message : 'PDF export failed');
                } finally {
                  setDownloadingPdf(false);
                }
              }}
              disabled={downloadingPdf}
              style={{
                appearance: 'none',
                padding: '6px 14px',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-input)',
                background: 'var(--accent)',
                color: 'var(--text-inverse)',
                fontSize: 'var(--font-size-2xs)',
                fontWeight: 500,
                cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                opacity: downloadingPdf ? 0.6 : 1,
              }}
              title="Download a methodology-pinned PDF matching the Reports export style. Includes footer stamp with methodology version + engagement id."
            >
              {downloadingPdf ? 'Preparing…' : 'Download PDF'}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                appearance: 'none',
                padding: '6px 14px',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-input)',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-2xs)',
                fontWeight: 500,
                cursor: 'pointer',
              }}
              title="Browser print — uses the on-screen stylesheet. For the audit-stamped export, use Download PDF."
            >
              Print
            </button>
          </div>
        }
      />
      {pdfError ? <Alert variant="error">{pdfError}</Alert> : null}

      <EngagementCard engagement={e} phase={phase} disputeCount={countDisputes(disputes)} />

      <VerdictsSection annotations={rows} disputes={disputes} slug={slug} />

      <DisputesSection annotations={rows} disputes={disputes} />

      <QaSummary threads={threads} />

      <RequestsSummary requests={reqs} />

      <footer
        style={{
          borderTop: '1px solid var(--border)',
          paddingTop: 'var(--space-3)',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
        }}
      >
        <span>
          Engagement <span style={{ fontFamily: 'var(--font-mono)' }}>{e.engagement_id}</span>
        </span>
        <span>
          Workpaper generated{' '}
          <time dateTime={new Date().toISOString()}>{fmtDate(new Date().toISOString())}</time>
        </span>
      </footer>
    </div>
  );
}

// ---- Engagement metadata card ---------------------------------------

function EngagementCard({
  engagement,
  phase,
  disputeCount,
}: {
  engagement: {
    workspace_id: string;
    assessor_firm: string;
    engagement_start: string;
    engagement_end: string;
    archive_window_days: number;
    revoked_at: string | null;
    revoked_reason: string | null;
    snapshot_id: string;
  };
  phase: EngagementPhase;
  disputeCount: number;
}) {
  const border = phaseBorder(phase);
  return (
    <section
      aria-label="Engagement metadata"
      style={{
        border: `1px solid ${border}`,
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, flex: 1, minWidth: 0 }}>
          {engagement.assessor_firm}
        </h2>
        <PhasePill phase={phase} />
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
          gap: 'var(--space-2) var(--space-4)',
          margin: 0,
          fontSize: 'var(--font-size-2xs)',
        }}
      >
        <MetaCell label="Engagement window">
          <time dateTime={engagement.engagement_start}>{fmtDate(engagement.engagement_start)}</time>
          {' → '}
          <time dateTime={engagement.engagement_end}>{fmtDate(engagement.engagement_end)}</time>
        </MetaCell>
        <MetaCell label="Archive window">{engagement.archive_window_days}d</MetaCell>
        <MetaCell label="Snapshot">
          <span style={{ fontFamily: 'var(--font-mono)' }}>{engagement.snapshot_id}</span>
        </MetaCell>
        <MetaCell label="Active disputes">
          <strong
            style={{
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
              color: disputeCount > 0 ? 'var(--danger-fg)' : 'var(--text)',
            }}
          >
            {disputeCount}
          </strong>
        </MetaCell>
      </dl>
      {engagement.revoked_reason ? (
        <p
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--band-red)',
            borderRadius: 'var(--radius-card)',
            fontSize: 'var(--font-size-3xs)',
          }}
        >
          <strong style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Revoked:</strong>{' '}
          {engagement.revoked_reason}
        </p>
      ) : null}
    </section>
  );
}

// ---- Verdicts section (grouped by verdict, dispute chips inline) ----

function VerdictsSection({
  annotations,
  disputes,
  slug,
}: {
  annotations: AssessorAnnotationRow[];
  disputes: Record<string, AssessorAnnotationDispute>;
  slug: string;
}) {
  const grouped = useMemo(() => {
    const map: Record<AssessorAnnotationVerdict, AssessorAnnotationRow[]> = {
      accepted: [],
      needs_remediation: [],
      not_applicable: [],
    };
    for (const row of annotations) map[row.verdict].push(row);
    return map;
  }, [annotations]);
  return (
    <section aria-labelledby="verdicts-heading" style={sectionStyle}>
      <SectionHeader
        id="verdicts-heading"
        title="Verdicts"
        counterLabel={`${annotations.length} active`}
      />
      {annotations.length === 0 ? (
        <EmptyLine>No verdicts have been recorded for this engagement yet.</EmptyLine>
      ) : (
        (['needs_remediation', 'accepted', 'not_applicable'] as const).map((v) => {
          const bucket = grouped[v];
          if (bucket.length === 0) return null;
          const label = VERDICT_TONE[v].label;
          return (
            <div key={v} style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-3xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--text-muted)',
                  fontWeight: 700,
                }}
              >
                {label} ({bucket.length})
              </h3>
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'grid',
                  gap: 'var(--space-2)',
                }}
              >
                {bucket.map((row) => (
                  <VerdictRow
                    key={row.annotation_id}
                    row={row}
                    dispute={disputes[row.annotation_id] ?? null}
                    slug={slug}
                  />
                ))}
              </ul>
            </div>
          );
        })
      )}
    </section>
  );
}

function VerdictRow({
  row,
  dispute,
  slug,
}: {
  row: AssessorAnnotationRow;
  dispute: AssessorAnnotationDispute | null;
  slug: string;
}) {
  const tone = VERDICT_TONE[row.verdict];
  const itemHref = `/w/${slug}/app/register/${row.register_item_id}`; // scope-check-ignore: dynamic workspace-scoped
  return (
    <li
      style={{
        border: `1px solid ${tone.border}`,
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: tone.bg,
        padding: 'var(--space-2) var(--space-3)',
        display: 'grid',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <VerdictPill verdict={row.verdict} />
        {dispute ? <WorkspaceDissentChip /> : null}
        <Link
          to={itemHref}
          style={{
            flex: 1,
            minWidth: 0,
            color: 'var(--text)',
            fontWeight: 500,
            fontSize: 'var(--font-size-2xs)',
            textDecoration: 'underline',
            textDecorationColor: 'var(--border-strong)',
          }}
        >
          {row.register_item_id}
        </Link>
        <time
          dateTime={row.annotated_at}
          style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}
        >
          {fmtDate(row.annotated_at)}
        </time>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
          color: tone.body,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {row.reason_text}
      </p>
    </li>
  );
}

function WorkspaceDissentChip() {
  return (
    <span
      title="This workspace has filed a counter-view on this verdict. See the Disputes section for the full reason."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: 'var(--warning-bg)',
        color: 'var(--warning-fg)',
        border: '1px solid var(--band-amber)',
      }}
    >
      Workspace dissent
    </span>
  );
}

// ---- Disputes section (full reason panel per active dispute) --------

function DisputesSection({
  annotations,
  disputes,
}: {
  annotations: AssessorAnnotationRow[];
  disputes: Record<string, AssessorAnnotationDispute>;
}) {
  const list = useMemo(() => {
    const rowsById = new Map(annotations.map((r) => [r.annotation_id, r]));
    return Object.values(disputes)
      .map((d) => ({ dispute: d, annotation: rowsById.get(d.annotation_id) ?? null }))
      .filter((x) => x.annotation != null);
  }, [annotations, disputes]);

  return (
    <section aria-labelledby="disputes-heading" style={sectionStyle}>
      <SectionHeader
        id="disputes-heading"
        title="Workspace disputes"
        counterLabel={`${list.length} active`}
      />
      {list.length === 0 ? (
        <EmptyLine>The workspace has not filed a dispute against any verdict.</EmptyLine>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 'var(--space-3)' }}>
          {list.map(({ dispute, annotation }) => (
            <li
              key={dispute.dispute_id}
              style={{
                border: '1px solid var(--band-amber)',
                borderLeftWidth: '3px',
                borderRadius: 'var(--radius-card)',
                background: 'var(--warning-bg)',
                color: 'var(--warning-fg)',
                padding: 'var(--space-3) var(--space-4)',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <VerdictPill verdict={dispute.disputed_verdict} />
                <span
                  style={{
                    fontSize: 'var(--font-size-3xs)',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {annotation!.register_item_id}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'right',
                    fontSize: 'var(--font-size-3xs)',
                    color: 'var(--text-dim)',
                    fontFamily: 'var(--font-mono)',
                  }}
                  title={dispute.disputed_by_user_id}
                >
                  {dispute.disputed_by_user_id} · <time dateTime={dispute.created_at}>{fmtDate(dispute.created_at)}</time>
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-2xs)',
                  color: 'var(--warning-fg)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {dispute.dispute_reason_text}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- Q&A summary ----------------------------------------------------

function QaSummary({ threads }: { threads: QaThreadRow[] }) {
  const grouped = useMemo(() => {
    const map = { open: 0, addressed: 0, resolved: 0 };
    for (const t of threads) map[t.status]++;
    return map;
  }, [threads]);
  return (
    <section aria-labelledby="qa-heading" style={sectionStyle}>
      <SectionHeader
        id="qa-heading"
        title="Q&A summary"
        counterLabel={`${threads.length} threads`}
      />
      {threads.length === 0 ? (
        <EmptyLine>No Q&A threads have been opened on this engagement.</EmptyLine>
      ) : (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
            gap: 'var(--space-2) var(--space-4)',
            margin: 0,
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          <StatCell label="Open" value={grouped.open} tone={grouped.open > 0 ? 'warning' : 'muted'} />
          <StatCell label="Addressed" value={grouped.addressed} tone="muted" />
          <StatCell label="Resolved" value={grouped.resolved} tone="muted" />
        </dl>
      )}
    </section>
  );
}

// ---- Evidence requests summary --------------------------------------

function RequestsSummary({ requests }: { requests: EvidenceRequestRow[] }) {
  const grouped = useMemo(() => {
    const map = { open: 0, evidence_provided: 0, closed: 0 };
    for (const r of requests) map[r.status]++;
    return map;
  }, [requests]);
  return (
    <section aria-labelledby="requests-heading" style={sectionStyle}>
      <SectionHeader
        id="requests-heading"
        title="Evidence requests"
        counterLabel={`${requests.length} total`}
      />
      {requests.length === 0 ? (
        <EmptyLine>No evidence requests have been opened on this engagement.</EmptyLine>
      ) : (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
            gap: 'var(--space-2) var(--space-4)',
            margin: 0,
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          <StatCell label="Open" value={grouped.open} tone={grouped.open > 0 ? 'warning' : 'muted'} />
          <StatCell label="Provided" value={grouped.evidence_provided} tone="muted" />
          <StatCell label="Closed" value={grouped.closed} tone="muted" />
        </dl>
      )}
    </section>
  );
}

// ---- Shared bits ----------------------------------------------------

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-card)',
  background: 'var(--surface)',
  padding: 'var(--space-4) var(--space-5)',
  display: 'grid',
  gap: 'var(--space-3)',
  breakInside: 'avoid',
};

function SectionHeader({
  id,
  title,
  counterLabel,
}: {
  id: string;
  title: string;
  counterLabel: string;
}) {
  return (
    <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <h2 id={id} style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, flex: 1, minWidth: 0 }}>
        {title}
      </h2>
      <span
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {counterLabel}
      </span>
    </header>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
      {children}
    </p>
  );
}

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
        {children}
      </dd>
    </div>
  );
}

function StatCell({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'muted' | 'warning' | 'danger';
}) {
  const toneMap = {
    muted: 'var(--text-dim)',
    warning: 'var(--warning-fg)',
    danger: 'var(--danger-fg)',
  };
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: toneMap[tone],
        }}
      >
        {value}
      </dd>
    </div>
  );
}

// ---- Verdict tokens (matches AssessorAnnotationsSection tone) ------

const VERDICT_TONE: Record<
  AssessorAnnotationVerdict,
  { label: string; bg: string; body: string; border: string; pillBg: string; pillFg: string }
> = {
  accepted: {
    label: 'Accepted',
    bg: 'var(--success-bg)',
    body: 'var(--success-fg)',
    border: 'var(--band-green)',
    pillBg: 'var(--band-green)',
    pillFg: 'var(--text-inverse)',
  },
  needs_remediation: {
    label: 'Needs remediation',
    bg: 'var(--danger-bg)',
    body: 'var(--danger-fg)',
    border: 'var(--band-red)',
    pillBg: 'var(--band-red)',
    pillFg: 'var(--text-inverse)',
  },
  not_applicable: {
    label: 'Not applicable',
    bg: 'var(--surface-alt)',
    body: 'var(--text-muted)',
    border: 'var(--border)',
    pillBg: 'var(--surface-alt)',
    pillFg: 'var(--text-muted)',
  },
};

function VerdictPill({ verdict }: { verdict: AssessorAnnotationVerdict }) {
  const t = VERDICT_TONE[verdict];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.pillBg,
        color: t.pillFg,
        border: `1px solid ${t.border}`,
      }}
    >
      {t.label}
    </span>
  );
}

function PhasePill({ phase }: { phase: EngagementPhase }) {
  const tone: Record<EngagementPhase, { bg: string; fg: string; label: string }> = {
    active: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Active' },
    read_only: {
      bg: 'var(--warning-bg)',
      fg: 'var(--warning-fg)',
      label: 'Read-only (archive)',
    },
    archive_expired: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)', label: 'Expired' },
    revoked: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', label: 'Revoked' },
  };
  const t = tone[phase];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function phaseBorder(phase: EngagementPhase): string {
  switch (phase) {
    case 'active':
      return 'var(--band-green)';
    case 'read_only':
      return 'var(--band-amber)';
    case 'archive_expired':
      return 'var(--border)';
    case 'revoked':
      return 'var(--band-red)';
  }
}

function countDisputes(disputes: Record<string, AssessorAnnotationDispute>): number {
  return Object.keys(disputes).length;
}

// ---- PDF renderer (PR-Cluster-6-m) ---------------------------------

/**
 * PDF-renderer palette — matches Reports.tsx: resolves DS tokens to
 * concrete hex at bundle time via the TypeScript scheme object,
 * since jsPDF renders straight to PDF bytes and can't read CSS
 * custom properties. Locked to the light palette (same escape
 * hatch as Reports.tsx per design-system README).
 */
const PDF_COLORS = {
  text: tokens.colors.light.text,
  textMuted: tokens.colors.light.textMuted,
  textDim: tokens.colors.light.textDim,
  accent: tokens.colors.light.accentCtaBg,
  success: tokens.colors.light.success,
  warning: tokens.colors.light.warning,
  critical: tokens.colors.light.critical,
  border: tokens.colors.light.border,
  borderStrong: tokens.colors.light.borderStrong,
} as const;

interface WorkpaperPdfInput {
  workspaceName: string;
  workspaceSlug: string;
  engagement: AssessorEngagementRow;
  phase: EngagementPhase;
  annotations: AssessorAnnotationRow[];
  disputes: Record<string, AssessorAnnotationDispute>;
  threads: QaThreadRow[];
  requests: EvidenceRequestRow[];
  methodology: ActiveMethodology;
}

function renderWorkpaperPdf(doc: jsPDF, input: WorkpaperPdfInput): void {
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = margin;

  function newPageIfNeeded(nextHeight = 20) {
    if (y + nextHeight > pageHeight - margin - 40) {
      doc.addPage();
      y = margin;
    }
  }

  function line(
    text: string,
    size = 10,
    opts: { bold?: boolean; italic?: boolean; color?: string; family?: 'helvetica' | 'times' } = {},
  ) {
    newPageIfNeeded(size * 1.4);
    doc.setFontSize(size);
    doc.setFont(
      opts.family ?? 'helvetica',
      opts.bold && opts.italic ? 'bolditalic' : opts.bold ? 'bold' : opts.italic ? 'italic' : 'normal',
    );
    doc.setTextColor(opts.color ?? PDF_COLORS.text);
    const wrapped = doc.splitTextToSize(text, pageWidth - margin * 2);
    doc.text(wrapped, margin, y);
    y += wrapped.length * (size * 1.35);
  }

  function spacer(px = 8) {
    y += px;
  }

  function divider() {
    newPageIfNeeded(12);
    doc.setDrawColor(PDF_COLORS.border);
    doc.line(margin, y, pageWidth - margin, y);
    y += 12;
  }

  // Wordmark + doc type
  doc.setFont('times', 'italic');
  doc.setFontSize(20);
  doc.setTextColor(PDF_COLORS.text);
  doc.text('gacpro', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(PDF_COLORS.textDim);
  doc.text('Assessor Engagement Workpaper', pageWidth - margin, y, { align: 'right' });
  y += 20;
  divider();

  // Engagement header
  line(`Workspace: ${input.workspaceName}`, 11, { bold: true });
  line(`Firm: ${input.engagement.assessor_firm}`, 9, { color: PDF_COLORS.textMuted });
  line(
    `Engagement: ${fmtDate(input.engagement.engagement_start)} → ${fmtDate(input.engagement.engagement_end)}   ·   Archive window ${input.engagement.archive_window_days}d   ·   Phase ${input.phase}`,
    9,
    { color: PDF_COLORS.textMuted },
  );
  line(
    `Methodology: v${input.methodology.version} · effective ${fmtDate(input.methodology.effective_at)} · hash ${input.methodology.formula_hash.slice(0, 24)}…`,
    9,
    { color: PDF_COLORS.textMuted, italic: true },
  );
  spacer(6);

  if (input.engagement.revoked_reason) {
    line(`⚠ REVOKED: ${input.engagement.revoked_reason}`, 9, {
      color: PDF_COLORS.critical,
      bold: true,
    });
    spacer(6);
  }

  // Verdicts summary
  const verdictBuckets = {
    accepted: 0,
    needs_remediation: 0,
    not_applicable: 0,
  };
  for (const r of input.annotations) verdictBuckets[r.verdict]++;
  const disputeCount = Object.keys(input.disputes).length;

  divider();
  line('Verdicts summary', 12, { bold: true });
  line(
    `${input.annotations.length} active verdicts · ${verdictBuckets.accepted} accepted · ${verdictBuckets.needs_remediation} needs remediation · ${verdictBuckets.not_applicable} not applicable`,
    9,
    { color: PDF_COLORS.textMuted },
  );
  if (disputeCount > 0) {
    line(
      `⚠ ${disputeCount} active workspace dispute${disputeCount === 1 ? '' : 's'} — see Disputes section.`,
      9,
      { color: PDF_COLORS.critical, bold: true },
    );
  }
  spacer(8);

  // Per-verdict sections, needs_remediation first (assessor's focus).
  for (const verdict of ['needs_remediation', 'accepted', 'not_applicable'] as const) {
    const bucket = input.annotations.filter((r) => r.verdict === verdict);
    if (bucket.length === 0) continue;
    const label = VERDICT_TONE[verdict].label;
    const color =
      verdict === 'accepted'
        ? PDF_COLORS.success
        : verdict === 'needs_remediation'
          ? PDF_COLORS.critical
          : PDF_COLORS.textMuted;

    newPageIfNeeded(30);
    line(`${label} (${bucket.length})`, 11, { bold: true, color });
    spacer(2);
    for (const row of bucket) {
      newPageIfNeeded(40);
      const disputed = input.disputes[row.annotation_id] != null;
      // Prefix with WORKSPACE-DISSENT marker so the chip's meaning
      // survives the PDF context — the workpaper reader can't
      // hover a color-only badge.
      line(
        `${disputed ? '[WORKSPACE DISSENT] ' : ''}${row.register_item_id} · ${fmtDate(row.annotated_at)}`,
        9,
        { bold: true, color: disputed ? PDF_COLORS.warning : PDF_COLORS.text },
      );
      line(row.reason_text, 9, { color: PDF_COLORS.textMuted });
      spacer(4);
    }
    spacer(4);
  }

  // Disputes section
  divider();
  line('Workspace disputes', 12, { bold: true });
  if (disputeCount === 0) {
    line('The workspace has not filed a dispute against any verdict.', 9, {
      color: PDF_COLORS.textMuted,
      italic: true,
    });
  } else {
    const rowsById = new Map(input.annotations.map((r) => [r.annotation_id, r]));
    for (const d of Object.values(input.disputes)) {
      const annotation = rowsById.get(d.annotation_id);
      if (!annotation) continue;
      newPageIfNeeded(50);
      line(
        `[${VERDICT_TONE[d.disputed_verdict].label}] ${annotation.register_item_id}   —  filed by ${d.disputed_by_user_id} on ${fmtDate(d.created_at)}`,
        9,
        { bold: true, color: PDF_COLORS.warning },
      );
      line(d.dispute_reason_text, 9, { color: PDF_COLORS.textMuted });
      spacer(6);
    }
  }
  spacer(6);

  // Q&A section — summary counters + full row list (PR-Cluster-6-n).
  divider();
  line('Q&A threads', 12, { bold: true });
  const qaBuckets = { open: 0, addressed: 0, resolved: 0 };
  for (const t of input.threads) qaBuckets[t.status]++;
  line(
    `${input.threads.length} thread${input.threads.length === 1 ? '' : 's'} · ${qaBuckets.open} open · ${qaBuckets.addressed} addressed · ${qaBuckets.resolved} resolved`,
    9,
    { color: PDF_COLORS.textMuted },
  );
  spacer(4);
  if (input.threads.length === 0) {
    line('No Q&A threads have been opened on this engagement.', 9, {
      color: PDF_COLORS.textDim,
      italic: true,
    });
  } else {
    // Sort open first (assessor's attention set), then addressed,
    // then resolved. Newest-first within each bucket.
    const sorted = [...input.threads].sort((a, b) => {
      const rank = { open: 0, addressed: 1, resolved: 2 } as const;
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      return Date.parse(b.opened_at) - Date.parse(a.opened_at);
    });
    for (const t of sorted) {
      newPageIfNeeded(24);
      const statusColor =
        t.status === 'open'
          ? PDF_COLORS.warning
          : t.status === 'addressed'
            ? PDF_COLORS.textMuted
            : PDF_COLORS.success;
      const statusLabel = t.status.toUpperCase();
      line(
        `[${statusLabel}] ${t.target_kind}/${t.target_id}   —   opened by ${t.opened_by} on ${fmtDate(t.opened_at)}`,
        9,
        { bold: true, color: statusColor },
      );
      if (t.status !== 'open' && t.resolved_at && t.resolved_reason) {
        line(
          `Resolved ${fmtDate(t.resolved_at)}${t.resolved_by ? ` by ${t.resolved_by}` : ''} — ${t.resolved_reason}`,
          9,
          { color: PDF_COLORS.textMuted, italic: true },
        );
      } else if (t.status !== 'open' && t.resolved_at) {
        line(
          `Resolved ${fmtDate(t.resolved_at)}${t.resolved_by ? ` by ${t.resolved_by}` : ''}`,
          9,
          { color: PDF_COLORS.textMuted, italic: true },
        );
      }
      spacer(4);
    }
  }
  spacer(6);

  // Evidence requests section — summary counters + full row list.
  divider();
  line('Evidence requests', 12, { bold: true });
  const reqBuckets = { open: 0, evidence_provided: 0, closed: 0 };
  for (const r of input.requests) reqBuckets[r.status]++;
  line(
    `${input.requests.length} request${input.requests.length === 1 ? '' : 's'} · ${reqBuckets.open} open · ${reqBuckets.evidence_provided} provided · ${reqBuckets.closed} closed`,
    9,
    { color: PDF_COLORS.textMuted },
  );
  spacer(4);
  if (input.requests.length === 0) {
    line('No evidence requests have been opened on this engagement.', 9, {
      color: PDF_COLORS.textDim,
      italic: true,
    });
  } else {
    // Sort by severity (critical first), then by status (open first),
    // then newest first. Reader lands on active-critical rows.
    const severityRank = { critical_finding: 0, clarification: 1 } as const;
    const statusRank = { open: 0, evidence_provided: 1, closed: 2 } as const;
    const sorted = [...input.requests].sort((a, b) => {
      const sa = severityRank[a.severity];
      const sb = severityRank[b.severity];
      if (sa !== sb) return sa - sb;
      const stA = statusRank[a.status];
      const stB = statusRank[b.status];
      if (stA !== stB) return stA - stB;
      return Date.parse(b.opened_at) - Date.parse(a.opened_at);
    });
    for (const r of sorted) {
      newPageIfNeeded(40);
      const severityColor =
        r.severity === 'critical_finding' ? PDF_COLORS.critical : PDF_COLORS.warning;
      const statusColor =
        r.status === 'open'
          ? PDF_COLORS.warning
          : r.status === 'closed'
            ? PDF_COLORS.textDim
            : PDF_COLORS.textMuted;
      line(
        `[${r.severity.toUpperCase().replace('_', ' ')}] ${r.register_item_id}   —   opened by ${r.opened_by} on ${fmtDate(r.opened_at)}`,
        9,
        { bold: true, color: severityColor },
      );
      line(`Status: ${r.status.replace('_', ' ')}`, 8, { color: statusColor });
      line(r.reason_text, 9, { color: PDF_COLORS.textMuted });
      if (r.provided_at) {
        line(
          `Provided ${fmtDate(r.provided_at)}${r.provided_by ? ` by ${r.provided_by}` : ''}${r.provided_note ? ` — ${r.provided_note}` : ''}`,
          8,
          { color: PDF_COLORS.textMuted, italic: true },
        );
      }
      if (r.closed_at && r.closed_reason) {
        line(
          `Closed ${fmtDate(r.closed_at)}${r.closed_by ? ` by ${r.closed_by}` : ''} — ${r.closed_reason}`,
          8,
          { color: PDF_COLORS.textMuted, italic: true },
        );
      }
      spacer(6);
    }
  }

  // Footer on every page — matches Reports.tsx pattern.
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(PDF_COLORS.border);
    doc.line(margin, pageHeight - margin - 30, pageWidth - margin, pageHeight - margin - 30);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(PDF_COLORS.textDim);
    doc.text(
      `gacpro · Assessor Workpaper · ${input.workspaceName} · engagement ${input.engagement.engagement_id}`,
      margin,
      pageHeight - margin - 16,
    );
    doc.text(
      `methodology v${input.methodology.version} · generated ${new Date().toISOString().slice(0, 10)} · page ${p} of ${pages}`,
      margin,
      pageHeight - margin - 4,
    );
  }
}
